// Business Profile builder — /app/business-profile (office roles)
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Globe, Copy, QrCode, Eye, Loader2, Plus, Trash2, ExternalLink, X, Check } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${API_BASE}/api/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`;
const TABS = ["Overview", "Edit Profile", "Projects", "Gallery", "Plant", "Accreditations", "Team", "Enquiries", "Settings"];
const ENQ_STATUSES = ["new", "contacted", "quoting", "won", "lost", "archived"];

async function upload(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", "business_profile");
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.storage_path;
}

export default function BusinessProfile() {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("Overview");
  useEffect(() => {
    api.get("/business-profile").then((r) => setP(r.data)).catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
  }, []);
  const save = async (patch, msg = "Saved") => {
    try { const { data } = await api.patch("/business-profile", patch); setP((old) => ({ ...old, ...data })); toast.success(msg); return data; }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (err) return (
    <div className="p-8 max-w-xl mx-auto text-center" data-testid="bp-disabled">
      <Globe className="w-8 h-8 mx-auto text-[#FF5F15]" />
      <h1 className="font-display font-black text-xl uppercase mt-3">Business Profiles</h1>
      <p className="text-sm text-zinc-500 mt-2">Create a public mini-website for your business — services, projects, plant and accreditations — and get found in the Fieldlink Contractor Directory.</p>
      <p className="text-xs text-zinc-400 mt-2">{err}</p>
      <button data-testid="bp-activate-btn"
        onClick={async () => {
          try { await api.post("/business-profile/activate"); toast.success("Business Profile activated!"); const { data } = await api.get("/business-profile"); setP(data); setErr(null); }
          catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); }
        }}
        className="mt-5 h-11 px-8 bg-[#FF5F15] hover:bg-[#E65100] text-white rounded-lg text-xs font-black uppercase tracking-wider">
        Activate Business Profile
      </button>
      <p className="text-[10.5px] text-zinc-400 mt-2">Included with any paid Fieldlink subscription.</p>
    </div>
  );
  if (!p) return <div className="p-8 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>;
  return (
    <div className="p-4 lg:p-6 max-w-[1300px] mx-auto pb-24 lg:pb-8" data-testid="business-profile-page">
      <h1 className="font-display font-black text-2xl uppercase tracking-tight">Business Profile</h1>
      <p className="text-[12px] text-zinc-500">Showcase your business and win more work.</p>
      <div className="flex gap-1 mt-3 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`bp-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={`h-8 px-3 rounded-md text-[11px] font-bold border ${tab === t ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`}>{t}</button>
        ))}
      </div>
      {tab === "Overview" && <OverviewTab p={p} setP={setP} />}
      {tab === "Edit Profile" && <EditTab p={p} save={save} />}
      {tab === "Projects" && <ProjectsTab />}
      {tab === "Gallery" && <GalleryTab p={p} save={save} />}
      {tab === "Plant" && <PlantTab p={p} save={save} />}
      {tab === "Accreditations" && <ListTab p={p} save={save} field="accreditations" label="Accreditation" fields={["name"]} withLogo />}
      {tab === "Team" && <ListTab p={p} save={save} field="team" label="Team member" fields={["name", "role"]} />}
      {tab === "Enquiries" && <EnquiriesTab />}
      {tab === "Settings" && <SettingsTab p={p} save={save} />}
    </div>
  );
}

function OverviewTab({ p, setP }) {
  const [an, setAn] = useState(null);
  const [qr, setQr] = useState(false);
  useEffect(() => { api.get("/business-profile/analytics").then((r) => setAn(r.data)).catch(() => {}); }, []);
  const url = `${window.location.origin}/company/${p.slug}`;
  const toggle = async () => {
    try {
      const { data } = await api.post(p.status === "live" ? "/business-profile/unpublish" : "/business-profile/publish");
      setP((old) => ({ ...old, status: data.status }));
      toast.success(data.status === "live" ? "Profile is LIVE" : "Profile unpublished");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const checks = [
    ["Company logo", !!p.logo_path], ["Cover photo", !!p.cover_path], ["About", (p.about || "").length > 20],
    ["Services", (p.services || []).length > 0], ["Contact details", !!(p.contact?.email || p.contact?.phone)],
    ["Gallery photos", (p.gallery || []).length > 2], ["Accreditations", (p.accreditations || []).length > 0],
  ];
  const pct = Math.round((checks.filter(([, ok]) => ok).length / checks.length) * 100);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <section className="hard-card p-5">
        <div className="label-tag mb-2">Profile status</div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${p.status === "live" ? "bg-emerald-500/15 text-emerald-500" : p.status === "suspended" ? "bg-red-500/15 text-red-500" : "bg-zinc-500/15 text-zinc-400"}`} data-testid="bp-status">{p.status}</span>
          {p.status !== "suspended" && (
            <Button size="sm" onClick={toggle} className={`h-8 rounded-lg text-[10px] font-bold uppercase tracking-wider ${p.status === "live" ? "bg-zinc-600" : "bg-[#FF5F15] hover:bg-[#E65100]"}`} data-testid="bp-publish-btn">
              {p.status === "live" ? "Unpublish" : "Publish profile"}
            </Button>
          )}
        </div>
        <div className="label-tag mt-4 mb-1.5">Public profile</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs font-mono bg-zinc-100 dark:bg-black/40 border border-zinc-300 dark:border-white/10 rounded px-2 py-1.5 break-all" data-testid="bp-public-url">{url}</code>
          <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} data-testid="bp-copy-link"><Copy className="w-3.5 h-3.5" /></Button>
          <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 rounded-lg" data-testid="bp-view-public"><Eye className="w-3.5 h-3.5" /></Button></a>
          <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => setQr(true)} data-testid="bp-qr-btn"><QrCode className="w-3.5 h-3.5" /></Button>
        </div>
        <Dialog open={qr} onOpenChange={setQr}>
          <DialogContent className="rounded-lg max-w-xs text-center">
            <DialogHeader><DialogTitle className="font-display uppercase">Profile QR code</DialogTitle></DialogHeader>
            <img alt="QR code" className="mx-auto rounded-lg bg-white p-2" width={220} height={220}
                 src={`https://api.qrserver.com/v1/create-qr-code/?size=440x440&data=${encodeURIComponent(url)}`} data-testid="bp-qr-img" />
            <a href={`https://api.qrserver.com/v1/create-qr-code/?size=880x880&download=1&data=${encodeURIComponent(url)}`} className="text-xs font-bold uppercase text-[#FF5F15]">Download QR</a>
            <p className="text-[10px] text-zinc-500">Use on utes, signage, business cards and quotes.</p>
          </DialogContent>
        </Dialog>
      </section>
      <section className="hard-card p-5">
        <div className="label-tag mb-2">Profile completeness</div>
        <div className="h-2.5 rounded-full bg-zinc-200 dark:bg-white/10 overflow-hidden"><div className="h-full bg-[#FF5F15]" style={{ width: `${pct}%` }} /></div>
        <div className="font-display font-black text-lg mt-1">{pct}%</div>
        <div className="mt-2 grid grid-cols-2 gap-1">
          {checks.map(([l, ok]) => (
            <div key={l} className={`text-[11.5px] flex items-center gap-1.5 ${ok ? "text-zinc-500" : "text-amber-500"}`}>
              {ok ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3 text-center">⚠</span>} {l}
            </div>
          ))}
        </div>
      </section>
      <section className="hard-card p-5 lg:col-span-2">
        <div className="label-tag mb-2">Last 30 days</div>
        <div className="grid grid-cols-3 lg:grid-cols-7 gap-2" data-testid="bp-analytics">
          {[["Profile views", an?.views], ["Search appearances", an?.search_appearances], ["Quote requests", an?.enquiries], ["Quote clicks", an?.quote_clicks],
            ["Contact clicks", an?.contact_clicks], ["Shares", an?.shares], ["Project views", an?.project_views]].map(([l, v]) => (
            <div key={l} className="rounded-md border border-zinc-300 dark:border-white/10 px-3 py-2">
              <div className="font-display font-black text-xl text-[#FF5F15]">{v ?? "—"}</div>
              <div className="text-[9.5px] font-bold uppercase tracking-widest text-zinc-500">{l}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EditTab({ p, save }) {
  const [f, setF] = useState({
    name: p.name || "", tagline: p.tagline || "", about: p.about || "",
    mission: p.mission || "", why_us: p.why_us || [], established_year: p.established_year || "", business_type: p.business_type || "",
    contact: { phone: "", email: "", website: "", location: "", ...(p.contact || {}) },
    stats: { years: "", team_size: "", projects_completed: "", based: "", ...(p.stats || {}) },
    services: p.services || [],
    specialist_services: p.specialist_services || {},
    service_areas: p.service_areas || [],
  });
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const analyse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/business-profile/analyse-website", { url });
      setFound(data);
      toast.success("We found information from your website — review below");
    } catch (e) { toast.error(e.response?.data?.detail || "We couldn't import this website — build manually below"); }
    finally { setBusy(false); }
  };
  const applyFound = () => {
    setF((o) => ({
      ...o,
      name: found.name || o.name, about: found.about || o.about,
      contact: { ...o.contact, phone: found.phone || o.contact.phone, email: found.email || o.contact.email, website: found.source_url || o.contact.website },
      services: [...new Set([...o.services.map((s) => s.title), ...(found.services || [])])].map((t) => ({ title: t })),
    }));
    toast.success("Suggestions applied — review and save");
  };
  const importImg = async (u, field) => {
    try {
      const { data } = await api.post("/business-profile/import-image", { url: u });
      await save({ [field]: data.storage_path }, field === "logo_path" ? "Logo imported" : "Cover imported");
    } catch { toast.error("Couldn't import image"); }
  };
  const uploadTo = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const path = await upload(file); await save({ [field]: path }); } catch { toast.error("Upload failed"); }
  };
  const toggleService = (t) => set("services", f.services.some((s) => s.title === t) ? f.services.filter((s) => s.title !== t) : [...f.services, { title: t }]);
  const [custom, setCustom] = useState("");
  return (
    <div className="space-y-4 max-w-[900px]">
      <section className="hard-card p-5">
        <div className="label-tag mb-1">Start with your website</div>
        <p className="text-[11.5px] text-zinc-500 mb-2">Paste your business website and we'll suggest your profile content. Nothing is published until you approve it.</p>
        <div className="flex gap-2 flex-wrap">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.yourcompany.co.nz" className="h-9 rounded-lg text-sm flex-1 min-w-[220px]" data-testid="bp-website-input" />
          <Button onClick={analyse} disabled={busy || url.length < 5} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase text-xs" data-testid="bp-analyse-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyse website"}
          </Button>
        </div>
        {found && (
          <div className="mt-3 border border-emerald-500/40 rounded-lg p-3 text-xs" data-testid="bp-found">
            <div className="font-bold text-emerald-500 uppercase tracking-wider text-[10px] mb-1.5">We found information from your website</div>
            {[["Company name", found.name], ["About us", found.about?.slice(0, 90)], ["Services", (found.services || []).join(", ")],
              ["Email", found.email], ["Phone", found.phone], ["Images", `${(found.image_urls || []).length} found`]].map(([l, v]) => v ? (
              <div key={l} className="flex gap-2 py-0.5"><Check className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" /><b>{l}:</b><span className="text-zinc-500 truncate">{v}</span></div>
            ) : null)}
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" onClick={applyFound} className="h-7 rounded text-[10px] font-bold uppercase bg-[#FF5F15] hover:bg-[#E65100]" data-testid="bp-apply-found">Review & apply suggestions</Button>
              {found.logo_url && <Button size="sm" variant="outline" className="h-7 rounded text-[10px] uppercase" onClick={() => importImg(found.logo_url, "logo_path")}>Use found logo</Button>}
              {found.cover_image_url && <Button size="sm" variant="outline" className="h-7 rounded text-[10px] uppercase" onClick={() => importImg(found.cover_image_url, "cover_path")}>Use found cover</Button>}
            </div>
            {(found.image_urls || []).length > 0 && (
              <div className="flex gap-1.5 mt-2 overflow-x-auto">
                {found.image_urls.slice(0, 8).map((u) => (
                  <button key={u} onClick={() => importImg(u, "cover_path")} title="Use as cover">
                    <img src={u} alt="" className="h-14 w-20 object-cover rounded border border-zinc-300 dark:border-white/10" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <section className="hard-card p-5 grid sm:grid-cols-2 gap-3">
        <div><div className="label-tag mb-1">Business name</div><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="h-9 rounded-lg" data-testid="bp-name" /></div>
        <div><div className="label-tag mb-1">Tagline</div><Input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Civil construction that delivers." className="h-9 rounded-lg" data-testid="bp-tagline" /></div>
        <div className="sm:col-span-2"><div className="label-tag mb-1">About us</div><Textarea value={f.about} onChange={(e) => set("about", e.target.value)} rows={4} className="rounded-lg" data-testid="bp-about" /></div>
        <div className="sm:col-span-2"><div className="label-tag mb-1">Our approach / mission <span className="text-zinc-500 normal-case">(optional second paragraph)</span></div><Textarea value={f.mission} onChange={(e) => set("mission", e.target.value)} rows={3} className="rounded-lg" placeholder="What you stand for — how you work with clients, quality, safety…" data-testid="bp-mission" /></div>
        <div><div className="label-tag mb-1">Established year <span className="text-zinc-500 normal-case">(optional)</span></div><Input value={f.established_year} onChange={(e) => set("established_year", e.target.value)} placeholder="2014" className="h-9 rounded-lg" data-testid="bp-established" /></div>
        <div><div className="label-tag mb-1">Business type <span className="text-zinc-500 normal-case">(optional)</span></div><Input value={f.business_type} onChange={(e) => set("business_type", e.target.value)} placeholder="Civil Contractor" className="h-9 rounded-lg" data-testid="bp-business-type" /></div>
        <div className="sm:col-span-2"><div className="label-tag mb-1">Why choose us <span className="text-zinc-500 normal-case">(one point per line, optional)</span></div>
          <Textarea value={(f.why_us || []).join("\n")} onChange={(e) => set("why_us", e.target.value.split("\n"))} rows={3} className="rounded-lg" placeholder={"Locally owned and operated\nExperienced, ticketed crews\nSafety-first on every site"} data-testid="bp-why-us" /></div>
        {[["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["location", "Primary location"]].map(([k, l]) => (
          <div key={k}><div className="label-tag mb-1">{l}</div>
            <Input value={f.contact[k] || ""} onChange={(e) => set("contact", { ...f.contact, [k]: e.target.value })} className="h-9 rounded-lg" data-testid={`bp-contact-${k}`} /></div>
        ))}
        {[["years", "Years experience"], ["team_size", "Team size"], ["projects_completed", "Projects completed"], ["based", "Based in"]].map(([k, l]) => (
          <div key={k}><div className="label-tag mb-1">{l} <span className="text-zinc-500 normal-case">(public — optional)</span></div>
            <Input value={f.stats[k] || ""} onChange={(e) => set("stats", { ...f.stats, [k]: e.target.value })} className="h-9 rounded-lg" data-testid={`bp-stat-${k}`} /></div>
        ))}
        <div><div className="label-tag mb-1">Logo</div>
          <div className="flex items-center gap-2">{p.logo_path && <img src={img(p.logo_path)} alt="logo" className="h-10 rounded border border-zinc-300 dark:border-white/10 bg-white p-0.5" />}
            <label className="text-[10px] font-bold uppercase text-[#FF5F15] cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTo(e, "logo_path")} data-testid="bp-logo-upload" /></label></div></div>
        <div><div className="label-tag mb-1">Cover image</div>
          <div className="flex items-center gap-2">{p.cover_path && <img src={img(p.cover_path)} alt="cover" className="h-10 w-20 object-cover rounded border border-zinc-300 dark:border-white/10" />}
            <label className="text-[10px] font-bold uppercase text-[#FF5F15] cursor-pointer">Upload<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTo(e, "cover_path")} data-testid="bp-cover-upload" /></label></div></div>
      </section>
      <section className="hard-card p-5">
        <div className="label-tag mb-2">Services & capabilities</div>
        <div className="flex flex-wrap gap-1.5">
          {(p.service_options || []).map((t) => (
            <button key={t} onClick={() => toggleService(t)} data-testid={`bp-svc-${t.toLowerCase().replace(/ /g, "-")}`}
              className={`h-7 px-2.5 rounded-md text-[10.5px] font-bold border ${f.services.some((s) => s.title === t) ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`}>{t}</button>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom service…" className="h-8 rounded-lg text-xs w-56" data-testid="bp-custom-service" />
          <Button size="sm" variant="outline" className="h-8 rounded-lg text-[10px] uppercase" onClick={() => { if (custom.trim()) { toggleService(custom.trim()); setCustom(""); } }}>Add</Button>
        </div>
        {/* specialist services from taxonomy — drives directory search matching */}
        {Object.keys(p.service_taxonomy || {}).length > 0 && (
          <div className="mt-4">
            <div className="label-tag mb-1">Specialist services</div>
            <p className="text-[11px] text-zinc-500 mb-2">Your selected services determine which Fieldlink contractor searches your business appears in.</p>
            {Object.entries(p.service_taxonomy).map(([cat, subs]) => {
              const chosen = f.specialist_services[cat] || [];
              return (
                <div key={cat} className="mb-2">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-400">{cat}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {subs.map((s) => (
                      <button key={s} data-testid={`bp-spec-${s.toLowerCase().replace(/ /g, "-")}`}
                        onClick={() => set("specialist_services", { ...f.specialist_services, [cat]: chosen.includes(s) ? chosen.filter((x) => x !== s) : [...chosen, s] })}
                        className={`h-6 px-2 rounded text-[10px] font-bold border ${chosen.includes(s) ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-4">
          <div className="label-tag mb-1">Service areas</div>
          <p className="text-[11px] text-zinc-500 mb-1.5">Regions/areas you service — used for "Where?" directory searches. No exact address needed.</p>
          <Input value={f.service_areas.join(", ")} onChange={(e) => set("service_areas", e.target.value.split(",").map((x) => x.trimStart()))}
            placeholder="Auckland, West Auckland, North Shore" className="h-9 rounded-lg text-xs" data-testid="bp-service-areas" />
        </div>
      </section>
      <Button onClick={() => save({ name: f.name, tagline: f.tagline, about: f.about, mission: f.mission, why_us: (f.why_us || []).map((w) => w.trim()).filter(Boolean), established_year: f.established_year, business_type: f.business_type, contact: f.contact, stats: f.stats, services: f.services, specialist_services: f.specialist_services, service_areas: f.service_areas.map((a) => a.trim()).filter(Boolean) })}
        className="h-10 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider text-xs px-8" data-testid="bp-save">Save profile</Button>
    </div>
  );
}

function ProjectsTab() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/business-profile/projects").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/business-profile/source-jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);
  const del = async (id) => { await api.delete(`/business-profile/projects/${id}`); toast.success("Removed"); load(); };
  return (
    <div>
      <Button onClick={() => setEdit({})} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-xs font-bold uppercase mb-3" data-testid="bp-add-project"><Plus className="w-4 h-4 mr-1" /> Add showcase project</Button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((pr) => (
          <div key={pr.id} className="hard-card overflow-hidden" data-testid={`bp-project-${pr.id}`}>
            {pr.cover_path && <img src={img(pr.cover_path)} alt="" className="h-32 w-full object-cover" />}
            <div className="p-3">
              <div className="font-display font-bold uppercase text-sm">{pr.name}</div>
              <div className="text-[11px] text-zinc-500">{[pr.category, pr.location].filter(Boolean).join(" • ")}</div>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${pr.published ? "bg-emerald-500/15 text-emerald-500" : "bg-zinc-500/15 text-zinc-400"}`}>{pr.published ? "Published" : "Private"}</span>
                <button onClick={() => setEdit(pr)} className="text-[10px] font-bold uppercase text-[#FF5F15] ml-auto">Edit</button>
                <button onClick={() => del(pr.id)} className="text-zinc-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-zinc-500 col-span-3">No showcase projects yet. Select a completed job and build a public showcase — internal job data stays private.</p>}
      </div>
      {edit && <ShowcaseDialog init={edit} jobs={jobs} onClose={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function ShowcaseDialog({ init, jobs, onClose }) {
  const [f, setF] = useState({ job_id: init.job_id || "", name: init.name || "", category: init.category || "", location: init.location || "",
    description: init.description || "", services: init.services || [], cover_path: init.cover_path || "", photos: init.photos || [], published: init.id ? !!init.published : true });
  const [photos, setPhotos] = useState([]);
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  useEffect(() => {
    if (f.job_id) api.get("/business-profile/source-photos", { params: { job_id: f.job_id } }).then((r) => setPhotos(r.data)).catch(() => {});
  }, [f.job_id]);
  const pickJob = (jid) => {
    const j = jobs.find((x) => x.id === jid);
    setF((o) => ({ ...o, job_id: jid, name: o.name || j?.title || "" }));
  };
  const togglePhoto = (path) => set("photos", f.photos.includes(path) ? f.photos.filter((x) => x !== path) : [...f.photos, path]);
  const submit = async () => {
    try {
      const body = { ...f, job_id: f.job_id || null };
      if (init.id) await api.patch(`/business-profile/projects/${init.id}`, body);
      else await api.post("/business-profile/projects", body);
      if (f.published) toast.success("Showcase saved — live on your public profile");
      else toast.warning("Saved as Private — flick the switch to show it on your public profile");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-lg max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">Public showcase project</DialogTitle></DialogHeader>
        <div className="space-y-2.5 text-sm">
          <div><div className="label-tag mb-1">Link to existing job (optional)</div>
            <Select value={f.job_id || "none"} onValueChange={(v) => pickJob(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-lg text-xs" data-testid="sc-job-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No linked job</SelectItem>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number} — {j.title}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><div className="label-tag mb-1">Public project name</div><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="h-9 rounded-lg" data-testid="sc-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><div className="label-tag mb-1">Project type</div><Input value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="Drainage" className="h-9 rounded-lg" data-testid="sc-category" /></div>
            <div><div className="label-tag mb-1">General location</div><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Auckland" className="h-9 rounded-lg" data-testid="sc-location" /></div>
          </div>
          <div><div className="label-tag mb-1">Description</div><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-lg" data-testid="sc-desc" /></div>
          {photos.length > 0 && (
            <div><div className="label-tag mb-1">Select photos from this job ({f.photos.length} selected)</div>
              <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto">
                {photos.map((ph) => (
                  <button key={ph.id} onClick={() => togglePhoto(ph.storage_path)} className={`relative rounded overflow-hidden border-2 ${f.photos.includes(ph.storage_path) ? "border-[#FF5F15]" : "border-transparent"}`} data-testid={`sc-photo-${ph.id}`}>
                    <img src={img(ph.storage_path)} alt="" className="h-16 w-full object-cover" />
                    {f.photos.includes(ph.storage_path) && <Check className="absolute top-0.5 right-0.5 w-4 h-4 text-white bg-[#FF5F15] rounded-full p-0.5" />}
                  </button>
                ))}
              </div>
              {f.photos.length > 0 && (
                <div className="mt-1.5"><div className="label-tag mb-1">Cover photo</div>
                  <div className="flex gap-1.5 overflow-x-auto">{f.photos.map((path) => (
                    <button key={path} onClick={() => set("cover_path", path)} className={`rounded overflow-hidden border-2 shrink-0 ${f.cover_path === path ? "border-[#FF5F15]" : "border-transparent"}`}>
                      <img src={img(path)} alt="" className="h-12 w-20 object-cover" /></button>))}</div></div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch checked={f.published} onCheckedChange={(v) => set("published", v)} data-testid="sc-published" />
            <span className="text-xs font-bold uppercase tracking-wider">{f.published ? "Published on profile" : "Private"}</span>
          </div>
          <Button onClick={submit} disabled={f.name.length < 2} className="w-full h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase text-xs" data-testid="sc-save">Save showcase</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GalleryTab({ p, save }) {
  const [photos, setPhotos] = useState([]);
  useEffect(() => { api.get("/business-profile/source-photos").then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  const gallery = p.gallery || [];
  const toggle = (path) => save({ gallery: gallery.some((g) => g.path === path) ? gallery.filter((g) => g.path !== path) : [...gallery, { path }] }, "Gallery updated");
  const uploadNew = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const path = await upload(file); await save({ gallery: [...gallery, { path }] }, "Photo added"); } catch { toast.error("Upload failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="label-tag">Public gallery ({gallery.length} photos)</div>
        <label className="text-[10px] font-bold uppercase text-[#FF5F15] cursor-pointer">+ Upload new<input type="file" accept="image/*" className="hidden" onChange={uploadNew} data-testid="bp-gallery-upload" /></label>
      </div>
      {gallery.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">{gallery.map((g) => (
          <div key={g.path} className="relative group"><img src={img(g.path)} alt="" className="h-24 w-full object-cover rounded" />
            <button onClick={() => toggle(g.path)} className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5 text-white" /></button></div>))}</div>
      )}
      <div className="label-tag">Pick from existing job photos (only selected photos become public)</div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 max-h-72 overflow-y-auto">
        {photos.map((ph) => {
          const selected = gallery.some((g) => g.path === ph.storage_path);
          return (
            <button key={ph.id} onClick={() => toggle(ph.storage_path)} className={`relative rounded overflow-hidden border-2 ${selected ? "border-[#FF5F15]" : "border-transparent"}`} data-testid={`bp-gal-${ph.id}`}>
              <img src={img(ph.storage_path)} alt="" className="h-16 w-full object-cover" />
              {selected && <Check className="absolute top-0.5 right-0.5 w-4 h-4 text-white bg-[#FF5F15] rounded-full p-0.5" />}
            </button>
          );
        })}
        {photos.length === 0 && <p className="text-xs text-zinc-500 col-span-7">No job photos in Fieldlink yet.</p>}
      </div>
    </div>
  );
}

function PlantTab({ p, save }) {
  const [src, setSrc] = useState(null);
  const pd = p.plant_display || { enabled: false, mode: "summary", categories: [], featured: [] };
  useEffect(() => { api.get("/business-profile/source-fleet").then((r) => setSrc(r.data)).catch(() => {}); }, []);
  const setPd = (patch) => save({ plant_display: { ...pd, ...patch } }, "Plant display updated");
  return (
    <div className="space-y-4 max-w-[700px]">
      <div className="flex items-center gap-2">
        <Switch checked={pd.enabled} onCheckedChange={(v) => setPd({ enabled: v })} data-testid="bp-plant-toggle" />
        <span className="text-xs font-bold uppercase tracking-wider">Show plant & capability publicly</span>
      </div>
      {pd.enabled && (
        <>
          <div className="flex gap-1.5">
            {[["summary", "Category summary"], ["featured", "Featured equipment"]].map(([v, l]) => (
              <button key={v} onClick={() => setPd({ mode: v })} className={`h-8 px-3 rounded-md text-[11px] font-bold border ${pd.mode === v ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`}>{l}</button>
            ))}
          </div>
          {pd.mode === "summary" && src && (
            <div>
              <p className="text-[11px] text-zinc-500 mb-2">Suggested from your fleet (source: Fieldlink fleet register — counts only, no regos/locations). Tick to approve for public display.</p>
              <div className="flex flex-wrap gap-1.5">
                {src.category_summary.map((c) => {
                  const on = pd.categories.some((x) => x.label === c.label);
                  return <button key={c.label} onClick={() => setPd({ categories: on ? pd.categories.filter((x) => x.label !== c.label) : [...pd.categories, c] })}
                    className={`h-8 px-3 rounded-md text-[11px] font-bold border ${on ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-300 dark:border-white/10 text-zinc-500"}`} data-testid={`bp-plant-cat-${c.label}`}>{c.count} {c.label}</button>;
                })}
              </div>
            </div>
          )}
          {pd.mode === "featured" && src && (
            <div>
              <p className="text-[11px] text-zinc-500 mb-2">Select equipment to feature (name + photo only — never rego, GPS or service history).</p>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {src.vehicles.map((v) => {
                  const nm = `${v.make || ""} ${v.model || ""}`.trim() || v.vehicle_type;
                  const on = pd.featured.some((x) => x.id === v.id);
                  return <button key={v.id} onClick={() => setPd({ featured: on ? pd.featured.filter((x) => x.id !== v.id) : [...pd.featured, { id: v.id, name: nm, photo_path: v.photo_path }] })}
                    className={`flex items-center gap-2 p-2 rounded-md border text-left text-xs ${on ? "border-[#FF5F15] bg-[#FF5F15]/5" : "border-zinc-300 dark:border-white/10"}`} data-testid={`bp-plant-veh-${v.id}`}>
                    {v.photo_path && <img src={img(v.photo_path)} alt="" className="h-8 w-12 object-cover rounded" />}
                    <span className="font-bold">{nm}</span>{on && <Check className="w-3.5 h-3.5 text-[#FF5F15] ml-auto" />}</button>;
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ListTab({ p, save, field, label, fields, withLogo }) {
  const items = p[field] || [];
  const [f, setF] = useState({});
  const add = async () => {
    if (!f.name?.trim()) return;
    await save({ [field]: [...items, f] }, `${label} added`);
    setF({});
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const path = await upload(file); setF((o) => ({ ...o, logo_path: path })); } catch { toast.error("Upload failed"); }
  };
  return (
    <div className="max-w-[560px] space-y-3">
      {field === "team" && <p className="text-[11px] text-zinc-500">Team is OFF by default and only selected people appear publicly. Field workers are never auto-exposed.</p>}
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 hard-card p-2.5 text-sm">
          {it.logo_path && <img src={img(it.logo_path)} alt="" className="h-8 w-8 object-contain rounded bg-white" />}
          <span className="font-bold">{it.name}</span><span className="text-zinc-500 text-xs">{it.role}</span>
          <button onClick={() => save({ [field]: items.filter((_, j) => j !== i) }, "Removed")} className="ml-auto text-zinc-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2 flex-wrap items-center">
        {fields.map((k) => <Input key={k} value={f[k] || ""} onChange={(e) => setF((o) => ({ ...o, [k]: e.target.value }))} placeholder={k === "name" ? `${label} name` : k} className="h-9 rounded-lg text-xs w-44" data-testid={`bp-${field}-${k}`} />)}
        {withLogo && <label className="text-[10px] font-bold uppercase text-[#FF5F15] cursor-pointer">{f.logo_path ? "✓ Logo" : "+ Logo"}<input type="file" accept="image/*" className="hidden" onChange={uploadLogo} /></label>}
        <Button size="sm" onClick={add} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-[10px] font-bold uppercase" data-testid={`bp-${field}-add`}>Add</Button>
      </div>
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/business-profile/enquiries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => { await api.patch(`/business-profile/enquiries/${id}`, body); load(); };
  const convert = async (id, kind) => {
    try {
      const { data } = await api.post(`/business-profile/enquiries/${id}/create-${kind}`);
      toast.success(kind === "client" ? `Client "${data.name}" created` : `Job ${data.job_number} created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-xs text-zinc-500">No enquiries yet. Share your public profile — quote requests will land here.</p>}
      {items.map((e) => (
        <div key={e.id} className="hard-card p-4" data-testid={`bp-enq-${e.id}`}>
          <div className="flex items-start gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold uppercase">{e.project_name || "Enquiry"}</div>
              <div className="text-xs text-zinc-500">{e.company && <b>{e.company} · </b>}{e.name} · {e.email} {e.phone && `· ${e.phone}`} {e.location && `· ${e.location}`}</div>
              {e.services?.length > 0 && <div className="text-[10px] font-bold uppercase text-[#FF5F15] mt-0.5">{e.services.join(" • ")}</div>}
              <p className="text-xs mt-1.5 whitespace-pre-wrap">{e.description}</p>
              {e.attachments?.length > 0 && (
                <div className="flex gap-2 mt-1.5 flex-wrap">{e.attachments.map((a, i) => (
                  <a key={i} href={img(a.storage_path)} target="_blank" rel="noreferrer" className="text-[10.5px] font-bold text-[#FF5F15] hover:underline">📎 {a.filename}</a>))}</div>
              )}
            </div>
            <Select value={e.status} onValueChange={(v) => patch(e.id, { status: v })}>
              <SelectTrigger className="h-8 w-[120px] rounded-lg text-xs" data-testid={`bp-enq-status-${e.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ENQ_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 mt-2.5 flex-wrap">
            {!e.client_id
              ? <Button size="sm" variant="outline" className="h-7 rounded text-[10px] font-bold uppercase" onClick={() => convert(e.id, "client")} data-testid={`bp-enq-client-${e.id}`}>Create client</Button>
              : <span className="text-[10px] font-bold uppercase text-emerald-500 self-center">✓ Client created</span>}
            {!e.job_id
              ? <Button size="sm" variant="outline" className="h-7 rounded text-[10px] font-bold uppercase" onClick={() => convert(e.id, "job")} data-testid={`bp-enq-job-${e.id}`}>Create job</Button>
              : <span className="text-[10px] font-bold uppercase text-emerald-500 self-center">✓ Job created</span>}
            <a href="/app/quotes" className="text-[10px] font-bold uppercase text-zinc-500 self-center hover:text-[#FF5F15]">Create quote <ExternalLink className="w-3 h-3 inline" /></a>
            <a href="/app/takeoffs" className="text-[10px] font-bold uppercase text-zinc-500 self-center hover:text-[#FF5F15]">Create takeoff <ExternalLink className="w-3 h-3 inline" /></a>
            <span className="text-[10px] text-zinc-500 ml-auto self-center">
              {e.source === "directory" ? "Fieldlink Contractor Directory" : "Fieldlink Business Profile"}
              {e.search_service ? ` · Search: ${e.search_service}${e.search_location ? " — " + e.search_location : ""}` : ""}
              {" · "}{new Date(e.created_at).toLocaleDateString("en-NZ")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ p, save }) {
  const [slug, setSlug] = useState(p.slug);
  return (
    <div className="max-w-[480px] hard-card p-5 space-y-3">
      <div className="label-tag">Public profile URL</div>
      <div className="flex items-center gap-1 text-sm"><span className="text-zinc-500">/company/</span>
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} className="h-9 rounded-lg" data-testid="bp-slug" /></div>
      <Button onClick={() => save({ slug }, "URL updated")} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-xs font-bold uppercase" data-testid="bp-slug-save">Update URL</Button>
      <div className="pt-3 border-t border-zinc-200 dark:border-white/10 flex items-center gap-2">
        <Switch checked={p.directory_listed !== false} onCheckedChange={(v) => save({ directory_listed: v }, v ? "Listed in directory" : "Removed from directory")} data-testid="bp-directory-toggle" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider">Appear in Fieldlink Directory</div>
          <div className="text-[10.5px] text-zinc-500">Show in public contractor searches (requires a live profile, at least one service and a service area).</div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">Section visibility is managed automatically — empty sections are hidden on your public page.</p>
      <div className="pt-3 border-t border-zinc-200 dark:border-white/10">
        <div className="label-tag mb-1.5">Auto SEO — how you appear on Google</div>
        <div className="border border-zinc-200 dark:border-white/10 rounded-lg p-3 bg-white dark:bg-zinc-900" data-testid="bp-seo-preview">
          <div className="text-[11px] text-emerald-700">www.fieldlink.co.nz › company › {p.slug}</div>
          <div className="text-[15px] text-blue-700 font-medium leading-snug mt-0.5">
            {p.name} | {(p.services || []).slice(0, 2).map((s) => s.title).join(" & ") || "Civil Contractor"} {p.contact?.location || "New Zealand"} | Fieldlink
          </div>
          <div className="text-[12px] text-zinc-600 dark:text-zinc-400 mt-0.5 line-clamp-2">
            {(p.about || `${p.name} — civil contractor. View services, projects and request a quote.`).slice(0, 158)}
          </div>
        </div>
        <p className="text-[10.5px] text-zinc-500 mt-1.5">Generated automatically from your name, services, location and About text — plus LocalBusiness structured data and a business sitemap submitted to search engines. Improve it by filling in your About and services.</p>
      </div>
    </div>
  );
}
